import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Textarea } from '@/components/atoms/textarea';
import { useLeadStages } from '@/hooks/leads/useLeadStages';
import { useUpdateLead } from '@/hooks/leads/useUpdateLead';
import type { JunkDisqualificationReason } from '@/types/lead';
import {
  isJunkDisqualificationReason,
  JUNK_DISQUALIFICATION_REASONS,
  resolveTerminalStage,
} from './support/disqualified-stage';

/**
 * A label for each junk reason. `Record` over the union rather than a loose map:
 * adding a reason to the shared list then fails the typecheck here instead of
 * rendering a selectable option with an empty label.
 */
const REASON_LABELS: Record<JunkDisqualificationReason, string> = {
  SPAM: 'Spam',
  DUPLICATE: 'Duplicate',
};

/**
 * Junk only — the reasons meaning the inquiry was never an opportunity, and
 * exactly what the server stores as `outcome: DISQUALIFIED`
 * (`isJunkDisqualification`). Built from the shared list rather than repeated,
 * so the dialog and the classifier cannot come to disagree about what is junk.
 *
 * The other six the API accepts (NO_BUDGET, COMPETITOR, OUT_OF_AREA, NOT_READY,
 * WRONG_SERVICE, OTHER) describe a real lead that did not close, so the server
 * records them as a plain LOST. Offering them here made this a second, worse way
 * to record a loss: it reaches `update` rather than the `close` endpoint, cannot
 * capture a competitor name, and asked for the loss in a dialog titled
 * "Disqualify". `MarkLeadLostModal` is the flow for that — it sits beside this
 * one at every entry point and carries the real loss vocabulary.
 */
const DISQUALIFICATION_REASONS = JUNK_DISQUALIFICATION_REASONS.map((value) => ({
  value,
  label: REASON_LABELS[value],
}));

interface DisqualifyLeadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  onSuccess?: () => void;
}

export function DisqualifyLeadModal({
  open,
  onOpenChange,
  leadId,
  onSuccess,
}: DisqualifyLeadModalProps) {
  const [reason, setReason] = useState<JunkDisqualificationReason | ''>('');
  const [notes, setNotes] = useState('');
  const updateLead = useUpdateLead();
  const stagesQuery = useLeadStages();
  // Keyed on the reason rather than fixed to the disqualified column. Every
  // reason this dialog offers is junk, so today it always resolves there — but
  // the rule that the column follows the reason is what keeps client and server
  // agreeing, and it survives a reason being added to the list.
  //
  // Before a reason is picked this resolves against the loss column, which the
  // gates below read. That cannot mis-gate them: both branches return
  // `undefined` under exactly one condition — the tenant has no active lost
  // stage at all — so the banners answer the same question either way.
  const terminalStage = resolveTerminalStage(stagesQuery.data, reason);
  // Gated on the resolved stage rather than on `isLoading`. Submitting without
  // one omits `stageId` and leaves the lead marked DISQUALIFIED while still
  // sitting in an open column — the exact failure this resolver exists to
  // prevent. Gating on the outcome covers all three ways it can be missing:
  // still loading, the query failed (v5 `isLoading` is already false by then,
  // and this hook suppresses its error toast), or the tenant has no active
  // lost stage at all.
  const canSubmit = !updateLead.isPending && reason !== '' && terminalStage !== undefined;
  // `isSuccess`, not `!isPending`: the latter is also true after a failed
  // fetch, which would blame the tenant's configuration for a network error.
  // This is the genuine misconfiguration — stages loaded fine and none of them
  // can hold a closed lead. Without it the button is simply dead, unexplained.
  const noLostStage = stagesQuery.isSuccess && terminalStage === undefined;
  // The other half of that split. Without it a failed fetch said nothing at
  // all: `noLostStage` is false, the toast is suppressed, and the disabled
  // button read as a permanently broken form rather than a retryable error.
  //
  // Gated on the resolved stage as well as on `isError`, for the same reason
  // `canSubmit` is: a refetch that fails AFTER a successful load leaves
  // `isError` true while `data` still holds the last good stages, so the form
  // works. Reporting "this lead can't be filed" there would be false, and
  // would push the user to retry something that is not blocking them. The
  // message belongs to the state where the stage genuinely cannot be
  // resolved — which is the initial-load failure.
  const stagesUnavailable = stagesQuery.isError && terminalStage === undefined;

  function resetForm() {
    setReason('');
    setNotes('');
  }

  function handleSubmit() {
    // Already gated on by the submit button; stated again so the empty initial
    // value narrows out of the union rather than being asserted away.
    if (reason === '') return;

    updateLead.mutate(
      {
        id: leadId,
        qualificationStatus: 'DISQUALIFIED',
        disqualificationReason: reason,
        disqualificationNotes: notes || undefined,
        ...(terminalStage ? { stageId: terminalStage.id } : {}),
      },
      {
        onSuccess: () => {
          resetForm();
          onOpenChange(false);
          onSuccess?.();
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) resetForm();
        onOpenChange(value);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Disqualify Lead</DialogTitle>
          <DialogDescription>
            Use this when the inquiry was never a real opportunity. For a real lead that didn’t
            close, use Mark lost instead.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="disqualification-reason">Reason</Label>
            {/* Radix hands back a bare string; the guard is what keeps state on
                the junk union rather than trusting the options rendered below. */}
            <Select
              value={reason}
              onValueChange={(value) => {
                if (isJunkDisqualificationReason(value)) setReason(value);
              }}
            >
              <SelectTrigger id="disqualification-reason">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {DISQUALIFICATION_REASONS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="disqualification-notes">Notes</Label>
            <Textarea
              id="disqualification-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Add any useful context..."
              rows={3}
            />
          </div>

          {noLostStage && (
            <p role="alert" className="text-sm text-destructive">
              No closed stage is configured, so this lead has nowhere to go. Add or reactivate a
              Lost or Disqualified stage in Settings → Leads, then try again.
            </p>
          )}

          {stagesUnavailable && (
            <div role="alert" className="space-y-2 text-sm text-destructive">
              <p>Couldn’t load lead stages, so this lead can’t be filed yet.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void stagesQuery.refetch()}
                disabled={stagesQuery.isFetching}
              >
                {stagesQuery.isFetching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Retry
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={!canSubmit}>
            {updateLead.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Disqualify
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
